// Copyright 2026 - Hewlett Packard Enterprise Company
import { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  getAuthority,
  OIDC_CLIENT_ID,
  WORKSPACE_ID_KEY,
  TENANT_ACID_KEY,
  CUSTOMER_ID_KEY,
  EMAIL_LINK_TENANT_KEY,
} from '../../constants';

/**
 * This component intercepts region switch navigation and clears stale session
 * data before AuthProvider initializes. When switching regions, each region has
 * its own applicationCustomerId, so we must discard the stale session and cid
 * to force GLCP to resolve the correct one for the new region.
 */
const RegionSwitchInterceptor = ({ children }) => {
  const hasProcessed = useRef(false);

  // Only process once per mount to avoid re-processing on re-renders
  if (!hasProcessed.current) {
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const isRegionSwitch = params.get('region_switch') === 'true';

    if (isRegionSwitch) {
      const queryWorkspaceId = params.get('workspace_id');

      // Restore workspace context from URL params
      if (queryWorkspaceId) {
        localStorage.setItem(WORKSPACE_ID_KEY, queryWorkspaceId);
      }

      // Always clear tenant acid on region switch — tenant ACIDs are
      // region-specific and invalid in the target region.
      localStorage.removeItem(TENANT_ACID_KEY);

      // Also clear any email link flag — region switch takes precedence
      sessionStorage.removeItem(EMAIL_LINK_TENANT_KEY);

      // Clear stale customer ID - forces GLCP to resolve the correct one
      localStorage.removeItem(CUSTOMER_ID_KEY);

      // Clear stale session token so the app re-authenticates with correct workspace
      const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
      const sessionKey = `oidc.user:${getAuthority(workspaceId)}:${OIDC_CLIENT_ID}`;
      sessionStorage.removeItem(sessionKey);

      // Strip region_switch params from URL so they don't persist or affect future navigation
      params.delete('region_switch');
      params.delete('workspace_id');
      params.delete('tenant_acid');
      const newSearch = params.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState(window.history.state ?? {}, '', newUrl);
    } else {
      // Email links pass tenant_acid in the URL to restore tenant context.
      // Store it before AuthProvider initializes so it picks up the value.
      // Set a sessionStorage flag so Login.js preserves it across the GLCP
      // auth round-trip.
      const queryTenantAcid = params.get('tenant_acid');
      if (queryTenantAcid) {
        localStorage.setItem(TENANT_ACID_KEY, queryTenantAcid);
        sessionStorage.setItem(EMAIL_LINK_TENANT_KEY, 'true');
        params.delete('tenant_acid');
        const newSearch = params.toString();
        const newUrl = newSearch
          ? `${window.location.pathname}?${newSearch}`
          : window.location.pathname;
        window.history.replaceState(window.history.state ?? {}, '', newUrl);
      }
    }
  }

  return children;
};

RegionSwitchInterceptor.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RegionSwitchInterceptor;
